import { GetStaticPaths, GetStaticProps } from "next";
import MainLayout from "../components/MainLayout";
import { promises as fs } from "fs";
import {
  CITY_FILE,
  FOOTER_FILE,
  MENUS_FILE,
  LEGAL_CONSULTATION_FILE,
} from "../constants/file-paths";
import Menus from "../dtos/Menus.dto";
import FooterDefinition from "../dtos/Footer.dto";
import { FC, useEffect, useMemo, useState } from "react";
import Head from "next/head";
import { ICity } from "../dtos/Home.dto";
import { allConcernedAreas } from "../constants/concernedAreasForLegalConsultation";
import { titleCase } from "../utils/titleCase";
import CityLawyerHeader from "../components/CityLawyer/CityLawyerHeader";
import { Row, Col } from "react-bootstrap";
import { CardStyled } from "../components/Card.styled";
import Text from "../styled/Text";
import LeadCityLawyerForm from "../components/CityLawyer/LeadCityLawyerForm";
import FindAllLawyersHeader from "../components/CityLawyer/FindAllLawyersHeader";
import Product from "../dtos/Product.dto";
import StickyFooterCityLawyers from "../components/StickyFooterCityLawyers";
import useResponsiveDevice from "../components/useResponsiveDevice";
import Spacer from "../styled/Spacer";

const CityLawyer: FC<{
  menus: any;
  footer: any;
  city: string;
  concernedArea?: string;
  title: string;
  product: Product;
}> = ({ footer, menus, city, concernedArea, title, product }) => {
  const [showMore, setShowMore] = useState(true);
  const { isMobile } = useResponsiveDevice();

  const urlYoutube = useMemo(() => {
    const videoUrls: {
      [key: string]: string;
    } = {
      business: "https://www.youtube.com/embed/HVniv7yoH70",
      divorce: "https://www.youtube.com/embed/WDgP-GaCb9k",
      will: "https://www.youtube.com/embed/jmg7PBnsAtE",
      default: "https://www.youtube.com/embed/OwRgqJPpggk",
    };

    return concernedArea
      ? videoUrls[concernedArea.toLowerCase().replace(/ /g, "-")] || ""
      : videoUrls["default"];
  }, [concernedArea]);

  useEffect(() => {
    setShowMore(false);
  }, []);

  const handleClickShowMore = () => setShowMore(!showMore);

  return (
    <MainLayout menus={menus} headerBgColorBack="skyBlue" footer={footer}>
      <Head>
        <title>{title}</title>
        <meta
          name="description"
          content={`Find the best ${
            concernedArea ? `${concernedArea} Lawyers` : "Lawyers"
          } in ${city}, consult top rated ${
            concernedArea ? `${concernedArea} Lawyers` : "Lawyers"
          } located in ${city} for your any legal issues.`}
        />
        <meta name="robots" content="index, follow" />
        <meta name="copyright" content="ezyLegal" />
        <meta name="reply-to" content={"contact@ezylegal.in"} />
        <meta
          name="keywords"
          content={`find${
            concernedArea ? ` ${String(concernedArea).replace(/-/g, " ")}` : ""
          } lawyers in ${city}`}
        />
      </Head>

      <FindAllLawyersHeader product={product} />

      <div className="p-4">
        <Row className="flex-row-reverse">
          <Col xs={12} md={5}>
            <Row
              style={{
                position: "sticky",
                top: 20,
                maxHeight: "95vh",
                overflowY: "auto",
              }}
            >
              <Col>
                <CardStyled className="shadow-sm">
                  <CardStyled.Header className="d-flex align-items-center justify-content-center">
                    <Text className="mb-0" fontSize="lg" weight="semibold">
                      Ask Lawyer
                    </Text>
                  </CardStyled.Header>
                  <CardStyled.Body>
                    <LeadCityLawyerForm />
                  </CardStyled.Body>
                </CardStyled>
              </Col>
            </Row>
          </Col>
          <Col xs={12} md={7}>
            {isMobile && <Spacer direction="vertical" size={20} />}
            <CityLawyerHeader
              city={city}
              concernedArea={concernedArea}
              urlYoutube={urlYoutube}
              showMore={showMore}
              handleClickShowMore={handleClickShowMore}
            />
          </Col>
        </Row>
      </div>
      {isMobile && <StickyFooterCityLawyers data={product} />}
    </MainLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const citiesData = await fs.readFile(CITY_FILE, { encoding: "utf-8" });
  const citiesAllCountries: ICity[] = JSON.parse(citiesData);
  const cities = citiesAllCountries.filter((city) => city.countryCode === "IN");

  const concernedAreas = allConcernedAreas
    .filter(
      (concernedArea, index) =>
        concernedArea !== "Other Legal Problem" &&
        concernedArea !== "Other Property Issues" &&
        concernedArea !== "Any other Business Legal matter" &&
        allConcernedAreas.indexOf(concernedArea) === index
    )
    .sort();

  let paths = [];
  for (const city of cities) {
    paths.push({
      params: {
        city_lawyer: `find-lawyers-in-${city.name
          .toLowerCase()
          .replace(/ /g, "-")}`,
        city: city.name,
      },
    });
  }

  for (const city of cities) {
    for (const concernedArea of concernedAreas) {
      paths.push({
        params: {
          city_lawyer: `find-${concernedArea
            .toLowerCase()
            .replace(/ /g, "-")}-lawyers-in-${city.name
            .toLowerCase()
            .replace(/ /g, "-")}`,
        },
      });
    }
  }

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const menuData = await fs.readFile(MENUS_FILE, { encoding: "utf-8" });
  const menus: Menus = JSON.parse(menuData);

  const footerData = await fs.readFile(FOOTER_FILE, { encoding: "utf-8" });
  const footer: FooterDefinition = JSON.parse(footerData);

  const city_lawyer = (context.params?.city_lawyer as string) || "";

  const legalConsultationData = await fs.readFile(LEGAL_CONSULTATION_FILE, {
    encoding: "utf-8",
  });

  const legalConsultation = JSON.parse(legalConsultationData);

  legalConsultation.slug = "legal-consultation-b";

  const product = legalConsultation;

  delete product.seo;
  delete product.getTalkTime;

  const city = city_lawyer.includes("find-lawyers-in-")
    ? titleCase(city_lawyer.replace("find-lawyers-in-", "").replace(/-/g, " "))
    : titleCase(city_lawyer.split("in-")[1].replace(/-/g, " "));

  const concernedArea = !city_lawyer.includes("find-lawyers-in-")
    ? titleCase(
        city_lawyer
          .replace("find-", "")
          .replace(/-/g, " ")
          .split("lawyers-in-")[0]
      ).split(" Lawyers In")[0]
    : "";

  const title = concernedArea
    ? `${concernedArea} Lawyers in ${city} | Ezylegal`
    : `Find Lawyers in ${city} | Ezylegal`;

  return {
    props: {
      menus,
      footer,
      city,
      concernedArea,
      product,
      title,
    },
  };
};

export default CityLawyer;
